import { useCallback, useMemo } from 'react';
import { useFeatureFlags } from '@funtech-int/yango-shared';
import { createSignal } from '@funtech-int/rival';

import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';

import { featureFlagsScheme } from '../../../../../../lib/featureFlags';
import { autoSkipHappened } from '../../../../hooks/tracker/use-episode-history';

interface UseIsAutoSkipEnabledProps {
    contentId: string;
}

const watchedTime = createSignal<number>(0);

export const clearAutopskipTimer = () => {
    watchedTime.set(0);
};

const useDayNight = () => {
    const autoskipDayNight = useFeatureFlags(featureFlagsScheme).flags.get('autoskipDayNight');

    return useCallback(() => {
        if (autoskipDayNight !== 'none') {
            return autoskipDayNight;
        }

        const hour = new Date().getHours();

        return hour >= 2 && hour <= 7 ? 'night' : 'day';
    }, [autoskipDayNight]);
};

const useWatchedTime = () => {
    const autoskipWacthedTime =
        useFeatureFlags(featureFlagsScheme).flags.get('autoskipWacthedTime');

    return useCallback(() => watchedTime.get() + autoskipWacthedTime, [autoskipWacthedTime]);
};

const useTimeToDisable = () =>
    useFeatureFlags(featureFlagsScheme).flags.get('autoskipTimeToDisable');

const useNightTimeToDisable = () =>
    useFeatureFlags(featureFlagsScheme).flags.get('autoskipNightTimeToDisable');

export const useIsAutoSkipEnabled = ({ contentId }: UseIsAutoSkipEnabledProps) => {
    const instance = usePlayerInstance();

    const getDayNight = useDayNight();
    const getWatchedTime = useWatchedTime();
    const timeToDisable = useTimeToDisable();
    const nightTimeToDisable = useNightTimeToDisable();

    const getIsAutoSkipEnabled = useCallback(() => {
        const watchedTime = getWatchedTime() + (instance?.watchedTime ?? 0);

        if (getDayNight() === 'day') {
            return watchedTime < timeToDisable;
        }

        if (getDayNight() === 'night') {
            return watchedTime < nightTimeToDisable;
        }

        return false;
    }, [getDayNight, getWatchedTime, instance, nightTimeToDisable, timeToDisable]);

    const autoSkipHappenedWrapped = useCallback(() => {
        watchedTime.set(watchedTime.get() + (instance?.watchedTime ?? 0));
        autoSkipHappened(contentId);
    }, [contentId, instance]);

    const isAutoSkipEnabled = useMemo(() => getIsAutoSkipEnabled(), [getIsAutoSkipEnabled]);

    return { isAutoSkipEnabled, autoSkipHappened: autoSkipHappenedWrapped, getIsAutoSkipEnabled };
};
